import { useCallback, useEffect, useState } from 'react'

/**
 * Escolha de microfone, câmera e saída de som da chamada (SPEC-20260916-2346).
 *
 * O navegador só entrega os NOMES dos aparelhos depois que a permissão foi
 * dada; antes disso a lista vem com rótulos vazios. Por isso a lista é
 * recarregada quando a chamada começa (já com permissão) e quando o sistema
 * avisa que algo foi plugado ou tirado (`devicechange`).
 *
 * A escolha fica no navegador, por pessoa e por computador — aparelho é do
 * computador, não da conta.
 */

export type TipoDeAparelho = 'microfone' | 'camera' | 'saida'
export type Aparelho = { id: string; nome: string }

const CHAVE: Record<TipoDeAparelho, string> = {
  microfone: 'orbind.chamada.microfone',
  camera: 'orbind.chamada.camera',
  saida: 'orbind.chamada.saida',
}

const TIPO_DOM: Record<TipoDeAparelho, MediaDeviceKind> = {
  microfone: 'audioinput',
  camera: 'videoinput',
  saida: 'audiooutput',
}

/** Id guardado, ou `''` quando é para usar o padrão do sistema. */
export function aparelhoEscolhido(tipo: TipoDeAparelho): string {
  try {
    return localStorage.getItem(CHAVE[tipo]) ?? ''
  } catch {
    return ''
  }
}

export function guardarAparelho(tipo: TipoDeAparelho, id: string): void {
  try {
    if (id) localStorage.setItem(CHAVE[tipo], id)
    else localStorage.removeItem(CHAVE[tipo])
  } catch {
    /* sem storage: vale só nesta chamada */
  }
}

/**
 * Restrição de mídia já com o aparelho escolhido (ou o padrão do sistema).
 *
 * `ideal` em tudo, nunca `exact` fora do aparelho: pedir exato faz a captura
 * FALHAR em quem não tem aquela combinação, e é melhor entrar na chamada com
 * imagem pior do que não entrar.
 */
export function restricaoDeAudio(): MediaTrackConstraints {
  const id = aparelhoEscolhido('microfone')
  return {
    ...(id ? { deviceId: { exact: id } } : {}),
    // O navegador liga isso por conta própria na maioria dos casos; declarar
    // evita depender do padrão de cada versão.
    echoCancellation: { ideal: true },
    noiseSuppression: { ideal: true },
    autoGainControl: { ideal: true },
  }
}

/**
 * Vídeo em 720p e 16:9, de propósito.
 *
 * Sem pedir resolução, o navegador entrega 640×480 em 4:3 — e numa câmera
 * 16:9 isso vem RECORTADO nas laterais, o que parece zoom: "a câmera pode
 * afastar mais, no Meet fica super boa" (usuário, 2026-09-17 23:31). Pedindo
 * 16:9 o sensor é usado inteiro, o enquadramento abre, e de quebra o recorte
 * do fundo trabalha sobre uma imagem com mais detalhe.
 */
export function restricaoDeVideo(): MediaTrackConstraints {
  const id = aparelhoEscolhido('camera')
  return {
    ...(id ? { deviceId: { exact: id } } : {}),
    width: { ideal: 1280 },
    height: { ideal: 720 },
    aspectRatio: { ideal: 16 / 9 },
    frameRate: { ideal: 30 },
  }
}

/** Manda o som para a caixa escolhida. Só o Chromium tem `setSinkId`. */
export async function aplicarSaida(elemento: HTMLMediaElement | null): Promise<void> {
  const id = aparelhoEscolhido('saida')
  const alvo = elemento as (HTMLMediaElement & { setSinkId?: (id: string) => Promise<void> }) | null
  if (!alvo?.setSinkId || !id) return
  try {
    await alvo.setSinkId(id)
  } catch {
    /* aparelho sumiu ou navegador não deixa: fica na saída padrão */
  }
}

/** Lista viva dos aparelhos de um tipo, com a escolha atual. */
export function useAparelhos(tipo: TipoDeAparelho) {
  const [lista, setLista] = useState<Aparelho[]>([])
  const [escolhido, setEscolhido] = useState(() => aparelhoEscolhido(tipo))

  const recarregar = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return
    try {
      const todos = await navigator.mediaDevices.enumerateDevices()
      setLista(
        todos
          .filter((d) => d.kind === TIPO_DOM[tipo] && d.deviceId)
          .map((d, i) => ({ id: d.deviceId, nome: d.label || `${rotuloPadrao(tipo)} ${i + 1}` })),
      )
    } catch {
      setLista([])
    }
  }, [tipo])

  useEffect(() => {
    void recarregar()
    const md = navigator.mediaDevices
    if (!md?.addEventListener) return
    const aoMudar = () => void recarregar()
    md.addEventListener('devicechange', aoMudar)
    return () => md.removeEventListener('devicechange', aoMudar)
  }, [recarregar])

  const escolher = useCallback(
    (id: string) => {
      guardarAparelho(tipo, id)
      setEscolhido(id)
    },
    [tipo],
  )

  return { lista, escolhido, escolher, recarregar }
}

function rotuloPadrao(tipo: TipoDeAparelho): string {
  return tipo === 'microfone' ? 'Microfone' : tipo === 'camera' ? 'Câmera' : 'Saída de som'
}
